// src/utils/logger.ts
type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

interface Logger {
  debug: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
}

const logLevels: Record<LogLevel, number> = {
  debug: 4,
  info: 3,
  warn: 2,
  error: 1,
  none: 0,
};

// Utility function to get UTC+9 time string
function getUTCTimeString(): string {
  const now = new Date();
  const utc9 = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return utc9.toISOString().replace('T', ' ').substring(0, 19); // YYYY-MM-DD HH:mm:ss
}

class LoggerFactory implements Logger {
  private currentLevel: number;

  constructor(level: LogLevel) {
    this.currentLevel = logLevels[level];
  }

  private logWithTimestamp(level: string, ...args: any[]) {
    const timestamp = getUTCTimeString();
    console.log(`[${level}] [${timestamp}]:`, ...args);
  }

  debug(...args: any[]) {
    if (this.currentLevel >= logLevels.debug) {
      this.logWithTimestamp('DEBUG', ...args);
    }
  }

  info(...args: any[]) {
    if (this.currentLevel >= logLevels.info) {
      this.logWithTimestamp('INFO', ...args);
    }
  }

  warn(...args: any[]) {
    if (this.currentLevel >= logLevels.warn) {
      this.logWithTimestamp('WARN', ...args);
    }
  }

  error(...args: any[]) {
    if (this.currentLevel >= logLevels.error) {
      this.logWithTimestamp('ERROR', ...args);
    }
  }
}

// Export an instance based on the environment
const envLogLevel: LogLevel = import.meta.env.VITE_LOG_LEVEL || 'info';
export const logger = new LoggerFactory(envLogLevel);
